import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Popover,
  TextField,
  Theme,
  Tooltip,
  Typography,
  AvatarGroup,
  avatarClasses,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import PersonAddOutlined from '@mui/icons-material/PersonAddOutlined';
import Search from '@mui/icons-material/Search';
import classNames from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import {
  ActionPlanCollaboratorSchema,
  ActionPlanCollaboratorsSchema,
  ActionPlanTaskOwnersSchema,
  ContactSchema,
} from '../../../../definitions/schemas';
import {
  filterOwnersOptionsBySearchCriteria,
  getOrderedOwnerOptions,
  isOwnerSelected,
  splitCollaboratorsByType,
} from './helper';
import { emptyOptionValue } from '../../../Users/helper';
import { findCollaborator, deduplicateCollaborators } from '../../helper';
import { getDisplayName } from '../../../../lib/DisplayName';
import { OrganizationFlagEnum } from '../../../../lib/features/FeatureFlag';
import { canDo } from '../../../../Services/GuardPermissionsService';
import RolesEnum from '../../../../enums/Roles';
import { OwnerAssignmentProps, OwnerInvitationProps } from './types';
import { SEARCH_DEBOUNCE_TIMEOUT } from '../../../../enums/Constraints';
import { stopEffects } from '../../../../lib/Event';
import { useI18n } from '../../../../lib/hook/useI18n';
import EditableAvatar from '../EditableAvatar';
import Link from '../../../Link';
import useDebouncedValue from '../../../../lib/hook/useDebounceValue';
import useFeatureFlag from '../../../../lib/hook/useFeatureFlag';
import withOwnerInvitations from './withOwnerInvite';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    color: theme.palette.secondary.main,
    textAlign: 'center',
  },
  avatar: {
    backgroundColor: 'var(--avatar-background-light)',
    color: theme.palette.text.secondary,
    height: theme.spacing(4),
    width: theme.spacing(4),
    ...theme.typography.body2,
    [`&.${avatarClasses.root}:last-child`]: {
      marginLeft: theme.spacing(-1),
    },
  },
  avatarGroupCentered: {
    justifyContent: 'right',
  },
  avatarGroupDisabled: {
    cursor: 'initial',
  },
  doneContainer: {
    bottom: 0,
    padding: theme.spacing(2),
    position: 'absolute',
    width: '100%',
  },
  listItem: {
    paddingLeft: theme.spacing(2),
  },
  listItemText: {
    cursor: 'text',
  },
  selector: {
    height: '100%',
    maxHeight: theme.spacing(37.5),
    maxWidth: '100%',
    overflowY: 'auto',
    marginBottom: theme.spacing(8),
    width: theme.spacing(45),
  },
}));

export interface OwnerSelectionProps
  extends OwnerAssignmentProps,
    OwnerInvitationProps {
  alignLeft?: boolean;
  disablePortal?: boolean;
  /**
   * Company to which the contacts belong to. This is used to categorize
   * the collaborators of type Contact. If there is no available company
   * name, then the default "Contacts" label is used.
   */
  companyName?: string;
  max?: number;
  /**
   * Name of the target organization where the collaborators of type User
   * belong to. This is the name of the org that owns the room.
   */
  organizationName?: string;
}

const OwnerSelection: React.FC<OwnerSelectionProps> = (
  props: OwnerSelectionProps
): JSX.Element => {
  const { t } = useI18n();
  const classes = useStyles();

  const areSmsNotificationsEnabled = useFeatureFlag(
    OrganizationFlagEnum.SMS_NOTIFICATIONS
  );
  const isRestrictUserVisibilityEnabled = useFeatureFlag(
    OrganizationFlagEnum.RELEASE_CONTROL_RESTRICT_USER_VISIBILITY
  );
  const isSalesperson = canDo(RolesEnum.SALES);
  const shouldRestrictSearch = isRestrictUserVisibilityEnabled && isSalesperson;

  const {
    alignLeft = false,
    allowInvitations = false,
    collaborators,
    companyName,
    disabled = false,
    disablePortal = false,
    hideOwnerAndDue = false,
    max,
    searchParticipants,
    onChange,
    onInviteOwner,
    options: initialOptions,
    organizationName,
    owners,
    setCollaborators,
  } = props;

  const [selectedOwners, setSelectedOwners] = useState(owners);
  const [selectedCollaborators, setSelectedCollaborators] =
    useState(collaborators);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] =
    useState<ActionPlanCollaboratorsSchema>([]);
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_TIMEOUT);

  /**
   * Combines options available only in-memory that cannot be fetched with
   * the `participants` query, for example: room's guests that were previously
   * selected in the select deal step (for room creation flow), invited
   * guests through the share room flow OR options the user selected.
   */
  const dedupedOwnersOptions = useMemo(() => {
    if (loading) {
      return [];
    }

    const filteredInMemoryOptions = filterOwnersOptionsBySearchCriteria(
      initialOptions,
      debouncedSearch
    );

    const deduped = deduplicateCollaborators(
      filteredInMemoryOptions,
      searchResults
    );

    return getOrderedOwnerOptions(deduped, owners, collaborators);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOptions, loading, owners, searchResults]);

  /**
   * Splits contact and users collaborators from the options, to render them
   * in separate categories in the owner selection popover
   */
  const [contacts, users] = useMemo(
    () => splitCollaboratorsByType(dedupedOwnersOptions),
    [dedupedOwnersOptions]
  );

  const ref = useRef<HTMLDivElement>(null);

  const isSearching = search.trim().length === 0;
  let placeholder = areSmsNotificationsEnabled
    ? t('map.task.owner.search_with_mobile_phone')
    : t('map.task.owner.search');
  if (shouldRestrictSearch) {
    placeholder = t('map.task.owner.search_exact_email');
  }

  useEffect(() => {
    if (open) {
      searchOwnersOptions(debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, open]);

  const searchOwnersOptions = async (search: string) => {
    const results = await searchParticipants(search);
    setSearchResults(results);

    setLoading(false);
  };

  const toggleOpen = () => {
    if (disabled) {
      return;
    }

    clearSearch();

    const isOpen = !open;
    setOpen(isOpen);

    if (!isOpen) {
      // when the owner selection is closed, we trigger the on change and
      // update the in-memory reference to selected collaborators
      setCollaborators((prev) =>
        deduplicateCollaborators(selectedCollaborators, prev)
      );

      return onChange(selectedOwners);
    }

    setSelectedOwners(owners);
  };

  const clearSearch = () => {
    setSearch('');
  };

  const handleOwnersInvited = (invitedOwners: ActionPlanTaskOwnersSchema) => {
    setSelectedOwners((prev) => [...prev, ...invitedOwners]);
  };

  const handleInviteOwner = () => {
    if (onInviteOwner) {
      onInviteOwner(search, handleOwnersInvited);
    }
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    setSearch(e.target.value);
  };

  const removeFromSelectedList = (option: ActionPlanCollaboratorSchema) => {
    setSelectedOwners((prev) =>
      prev.filter(
        ({ ownerId, ownerType }) =>
          !(ownerId === option.id && ownerType === option.collaboratorType)
      )
    );
  };

  const handleSelect =
    (option: ActionPlanCollaboratorSchema, isSelected: boolean) => () => {
      if (isSelected) {
        return removeFromSelectedList(option);
      }

      const owner = { ownerId: option.id, ownerType: option.collaboratorType };
      setSelectedOwners((prev) => [...prev, owner]);
      setSelectedCollaborators((prev) => [...prev, option]);
    };

  const renderInviteOption = (): JSX.Element | undefined => {
    if (!(allowInvitations && search)) {
      return undefined;
    }

    return (
      <ListItem className={classes.listItem} button onClick={handleInviteOwner}>
        <ListItemIcon>
          <Box color="secondary.main" px={1} mt={-0.5}>
            <Add color="inherit" />
          </Box>
        </ListItemIcon>
        <ListItemText primary={<Link text={`Add ${search}?`} />} />
      </ListItem>
    );
  };

  const renderEmptyState = (): JSX.Element | undefined => (
    <Box className={classes.listItem}>
      <Typography
        color="textSecondary"
        sx={{ textTransform: 'none' }}
        variant="body2"
      >
        No results found
      </Typography>
    </Box>
  );

  let component = (
    <IconButton color="inherit" disabled={disabled}>
      <PersonAddOutlined fontSize="small" />
    </IconButton>
  );

  if (!disabled) {
    component = (
      <Tooltip
        placement="top"
        PopperProps={{ disablePortal: true }}
        title={t('map.set_owner')}
        enterTouchDelay={0}
        leaveTouchDelay={5000}
      >
        <Box>{component}</Box>
      </Tooltip>
    );
  }

  if (owners?.length) {
    component = (
      <AvatarGroup
        classes={{
          root: classNames({
            [classes.avatarGroupCentered]: !alignLeft,
            [classes.avatarGroupDisabled]: disabled,
          }),
          avatar: classes.avatar,
        }}
        max={max || 2}
        role="button"
      >
        {owners.map(({ ownerId, ownerType }) => {
          const key = `${ownerType}-${ownerId}`;
          const collaborator = findCollaborator(
            initialOptions,
            ownerId,
            ownerType
          );

          let name = '';
          if (collaborator) {
            name = getDisplayName(collaborator as ContactSchema, {
              fullEmail: true,
            });
          }

          return (
            <Tooltip
              key={key}
              placement="top"
              PopperProps={{ disablePortal: true }}
              title={name}
              enterTouchDelay={0}
              leaveTouchDelay={5000}
            >
              <EditableAvatar
                alt={name}
                disabled={disabled}
                onEdit={toggleOpen}
                siblings={owners.length}
                src={collaborator?.photo}
              >
                {name[0]?.toUpperCase()}
              </EditableAvatar>
            </Tooltip>
          );
        })}
      </AvatarGroup>
    );
  }

  const renderOption = (option: ActionPlanCollaboratorSchema): JSX.Element => {
    const key = `${option.collaboratorType}-${option.id}`;
    const name = getDisplayName(option as ContactSchema, {
      fullEmail: true,
    });
    const selected = isOwnerSelected(option, selectedOwners);
    const isDisabled = option.id === emptyOptionValue;

    return (
      <ListItem
        key={key}
        button
        className={classes.listItem}
        disabled={isDisabled}
        onClick={handleSelect(option, selected)}
      >
        {!isDisabled && (
          <>
            <ListItemIcon>
              <Checkbox checked={selected} color="secondary" />
            </ListItemIcon>

            <ListItemAvatar>
              <Avatar alt={name} className={classes.avatar} src={option.photo}>
                {name[0]?.toUpperCase()}
              </Avatar>
            </ListItemAvatar>
          </>
        )}

        <ListItemText
          primary={name}
          primaryTypographyProps={{ noWrap: true, title: name }}
        />
      </ListItem>
    );
  };

  const renderCategory = (
    category: string | JSX.Element,
    options: ActionPlanCollaboratorsSchema
  ): JSX.Element | undefined => {
    const isSearchMoreOption = options[0] && options[0].id === emptyOptionValue;
    if (options.length === 0 || isSearchMoreOption) {
      return undefined;
    }

    return (
      <List disablePadding>
        <ListItem className={classes.listItem}>
          <ListItemText className={classes.listItemText}>
            <Box component="strong">{category}</Box>
          </ListItemText>
        </ListItem>
        {options.map((option) => renderOption(option))}
      </List>
    );
  };

  const renderContent = (): JSX.Element | undefined => {
    if (!loading && !allowInvitations && dedupedOwnersOptions.length === 0) {
      return <Box className={classes.selector}>{renderEmptyState()}</Box>;
    }

    return (
      <Box className={classes.selector}>
        {loading ? (
          <Box className={classes.listItem} sx={{ color: 'text.disabled' }}>
            Loading...
          </Box>
        ) : (
          <>
            {renderCategory(
              companyName || t('map.task.owner.contacts'),
              contacts
            )}
            {renderCategory(
              organizationName || t('map.task.owner.organization'),
              users
            )}
            {renderInviteOption()}
          </>
        )}
      </Box>
    );
  };

  return (
    <>
      {!hideOwnerAndDue && (
        <>
          <Box
            className={classes.container}
            onClick={stopEffects(toggleOpen)}
            ref={ref}
          >
            {component}
          </Box>

          <Popover
            anchorEl={ref.current}
            disablePortal={disablePortal}
            onClick={stopEffects()}
            onClose={toggleOpen}
            open={open}
          >
            <Box p={2}>
              <TextField
                autoFocus
                color="secondary"
                fullWidth
                InputProps={{
                  endAdornment: isSearching ? (
                    <Search />
                  ) : (
                    <IconButton onClick={clearSearch} size="small">
                      <Close />
                    </IconButton>
                  ),
                }}
                onChange={onSearchChange}
                placeholder={placeholder}
                size="small"
                value={search}
                variant="outlined"
              />
            </Box>
            {renderContent()}
            <Box
              className={classes.doneContainer}
              display="flex"
              justifyContent="flex-end"
              p={2}
            >
              <Button
                color="secondary"
                onClick={stopEffects(toggleOpen)}
                variant="contained"
              >
                {t('map.task.owner.done')}
              </Button>
            </Box>
          </Popover>
        </>
      )}
    </>
  );
};

export default withOwnerInvitations(OwnerSelection);
